import React, { useEffect, useRef, useState } from "react";
import UsersStyle from "./style";
import TabsList from "../../Reusable/Tabs";
import { Button, Col, Input, Row, Table, Form, Popover, Select, Modal, message, Radio, Checkbox } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  CloseOutlined,
  CheckOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import COLORS from "../../../common/Colors";
import styled from "styled-components";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import UseApi from "../../Reusable/CallApi/UseApi";

const StyledTable = styled(Table)`
  padding-top: 20px;
  table {
    width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    border: 1px solid #00000024;
    border-radius: 10px;
  }
  .ant-table-cell {
    padding: 20px;
  }
  .normal-column {
    min-width: 300px;
    white-space: normal;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    font-size: 0.9rem;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  .ant-table-cell-row-hover {
    background: transparent !important;
  }

  /* Row actions only reveal on hover, matching Figma - the actions
     column keeps its reserved width at all times (fixed: "right" on
     the column) so toggling opacity here never reflows the row the
     way toggling the icons' presence/width used to. */
  .table_action_icons,
  .row-remove-link {
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  .ant-table-tbody > tr:hover .table_action_icons,
  .ant-table-tbody > tr:hover .row-remove-link {
    opacity: 1;
  }
`;

const ViewCourse = ({ tableViewData, setUserRenderView }) => {
  const [activeTab, setActiveTab] = useState("1");
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const courseId = tableViewData?.course_id;

  const { data: enrollmentsData, loading: enrollmentsLoading, request: enrollmentsRequest } = UseApi();
  const { data: groupsData, loading: groupsLoading, request: groupsRequest } = UseApi();
  const { request: peopleRequest } = UseApi();
  const { request: allGroupsRequest } = UseApi();
  const { request: enrollRequest } = UseApi();
  const { request: addGroupRequest } = UseApi();
  const { request: removeGroupRequest } = UseApi();
  const { request: unenrollRequest } = UseApi();
  const { request: updateEnrollmentRequest } = UseApi();
  const { data: filesData, loading: filesLoading, request: filesRequest } = UseApi();
  const { request: uploadUrlRequest } = UseApi();
  const { request: addFileRequest } = UseApi();
  const { request: deleteFileRequest } = UseApi();

  // Enroll Employees - two-step flow (choose list vs. by category, then
  // multi-select) per the Figma "Enroll Employees" modal - the old
  // single-person Select only ever enrolled one person per click.
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [enrollStep, setEnrollStep] = useState(1);
  const [enrollMode, setEnrollMode] = useState("list");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [people, setPeople] = useState([]);
  const [selectedPeopleIds, setSelectedPeopleIds] = useState([]);
  const [enrolling, setEnrolling] = useState(false);

  // Add to Group - multi-select modal per Figma, replacing the old
  // one-click-at-a-time drawer.
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [addingGroups, setAddingGroups] = useState(false);

  // Upload File - drag/drop + staged multi-file list per Figma, replacing
  // the old instant single-file-picker-and-upload.
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [savingFiles, setSavingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const navigate = useNavigate();

  const fetchEnrollments = () => {
    if (!clientId || !courseId) return;
    enrollmentsRequest({
      endPoint: `/course/getcourseenrollments/${clientId}/${courseId}`,
      method: "get",
    }).catch(() => {});
  };

  const fetchCourseGroups = () => {
    if (!clientId || !courseId) return;
    groupsRequest({
      endPoint: `/course/getcoursegroups/${clientId}/${courseId}`,
      method: "get",
    }).catch(() => {});
  };

  const fetchCourseFiles = () => {
    if (!clientId || !courseId) return;
    filesRequest({
      endPoint: `/course/getcoursefiles/${clientId}/${courseId}`,
      method: "get",
    }).catch(() => {});
  };

  useEffect(() => {
    fetchEnrollments();
    fetchCourseGroups();
    fetchCourseFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, courseId]);

  const enrolledPeople = enrollmentsData?.response || [];
  const courseGroups = groupsData?.response || [];
  const courseFiles = filesData?.response || [];

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "---");

  const formatFileSize = (bytes) => {
    if (bytes == null) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ---- People tab row actions -------------------------------------------------

  const handleUnenroll = async (record) => {
    try {
      await unenrollRequest({
        endPoint: `/course/unenroll/${clientId}/${courseId}?people_id=${record.people_id}`,
        method: "delete",
      });
      message.success("Employee removed from course");
      fetchEnrollments();
    } catch (error) {
      message.error("Failed to remove employee");
    }
  };

  const handleMarkComplete = async (record) => {
    try {
      await updateEnrollmentRequest({
        endPoint: `/course/updateenrollment/${clientId}/${record.enrollment_id}`,
        method: "patch",
        data: { progress: 100, status: "completed", completed_at: new Date().toISOString() },
      });
      message.success("Marked as completed");
      fetchEnrollments();
    } catch (error) {
      message.error("Failed to update enrollment");
    }
  };

  const handleResetProgress = async (record) => {
    try {
      await updateEnrollmentRequest({
        endPoint: `/course/updateenrollment/${clientId}/${record.enrollment_id}`,
        method: "patch",
        data: { progress: 0, status: "not_started", completed_at: null },
      });
      message.success("Progress reset");
      fetchEnrollments();
    } catch (error) {
      message.error("Failed to update enrollment");
    }
  };

  const handleRenewExpiry = async (record) => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    try {
      await updateEnrollmentRequest({
        endPoint: `/course/updateenrollment/${clientId}/${record.enrollment_id}`,
        method: "patch",
        data: { expiry_date: oneYearFromNow.toISOString().slice(0, 10) },
      });
      message.success("Expiry renewed by 1 year");
      fetchEnrollments();
    } catch (error) {
      message.error("Failed to update enrollment");
    }
  };

  // ---- Enroll Employees modal --------------------------------------------------

  const openEnrollModal = async () => {
    setEnrollStep(1);
    setEnrollMode("list");
    setCategoryFilter(null);
    setSelectedPeopleIds([]);
    setEnrollModalOpen(true);
    const result = await peopleRequest({ endPoint: `/people/getuser/${clientId}`, method: "get" }).catch(() => null);
    setPeople(result?.response || []);
  };

  const closeEnrollModal = () => setEnrollModalOpen(false);

  const alreadyEnrolledIds = new Set(enrolledPeople.map((p) => p.people_id));
  const enrollablePeople = people.filter((p) => !alreadyEnrolledIds.has(p.people_id));
  const categories = Array.from(new Set(enrollablePeople.map((p) => p.departrment).filter(Boolean)));
  const peopleForStep2 =
    enrollMode === "category" && categoryFilter
      ? enrollablePeople.filter((p) => p.departrment === categoryFilter)
      : enrollablePeople;

  const togglePersonSelected = (peopleId) => {
    setSelectedPeopleIds((prev) =>
      prev.includes(peopleId) ? prev.filter((id) => id !== peopleId) : [...prev, peopleId]
    );
  };

  const toggleSelectAllPeople = (checked) => {
    setSelectedPeopleIds(checked ? peopleForStep2.map((p) => p.people_id) : []);
  };

  const handleBulkEnroll = async () => {
    if (selectedPeopleIds.length === 0) return;
    setEnrolling(true);
    try {
      for (const peopleId of selectedPeopleIds) {
        await enrollRequest({
          endPoint: `/course/enroll/${clientId}`,
          method: "post",
          data: { course_id: courseId, people_id: peopleId },
        });
      }
      message.success(`Enrolled ${selectedPeopleIds.length} employee(s)`);
      setEnrollModalOpen(false);
      fetchEnrollments();
    } catch (error) {
      message.error("Failed to enroll some employees");
    } finally {
      setEnrolling(false);
    }
  };

  // ---- Add to Group modal --------------------------------------------------

  const openGroupModal = async () => {
    setSelectedGroupIds([]);
    setGroupModalOpen(true);
    const result = await allGroupsRequest({ endPoint: `/group/getgroup/${clientId}`, method: "get" }).catch(() => null);
    setAllGroups(result?.response || []);
  };

  const closeGroupModal = () => setGroupModalOpen(false);

  const assignedGroupIds = new Set(courseGroups.map((g) => g.group_id));
  const selectableGroups = allGroups.filter((g) => !assignedGroupIds.has(g.group_id));

  const toggleGroupSelected = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleBulkAddGroup = async () => {
    if (selectedGroupIds.length === 0) return;
    setAddingGroups(true);
    try {
      for (const groupId of selectedGroupIds) {
        await addGroupRequest({
          endPoint: `/course/addcoursegroup/${clientId}/${courseId}`,
          method: "post",
          data: { group_id: groupId },
        });
      }
      message.success(`Added to ${selectedGroupIds.length} group(s)`);
      setGroupModalOpen(false);
      fetchCourseGroups();
    } catch (error) {
      message.error("Failed to add some groups");
    } finally {
      setAddingGroups(false);
    }
  };

  const handleRemoveGroup = async (groupId) => {
    try {
      await removeGroupRequest({
        endPoint: `/course/removecoursegroup/${clientId}/${courseId}?group_id=${groupId}`,
        method: "delete",
      });
      fetchCourseGroups();
    } catch (error) {
      message.error("Failed to remove group");
    }
  };

  // ---- Upload File modal --------------------------------------------------

  const openFileModal = () => {
    setStagedFiles([]);
    setFileModalOpen(true);
  };

  const closeFileModal = () => setFileModalOpen(false);

  const addStagedFiles = (fileList) => {
    setStagedFiles((prev) => [...prev, ...Array.from(fileList)]);
  };

  const removeStagedFile = (index) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveStagedFiles = async () => {
    if (stagedFiles.length === 0) return;
    setSavingFiles(true);
    try {
      for (const file of stagedFiles) {
        const urlResponse = await uploadUrlRequest({
          endPoint: `/course/getuploadurl/${clientId}`,
          method: "post",
          data: { file_name: file.name, content_type: file.type },
        });
        const { uploadUrl, fileUrl } = urlResponse?.response || {};
        if (!uploadUrl || !fileUrl) {
          throw new Error("No upload URL returned");
        }

        // Upload straight to S3 with the presigned URL - no auth header.
        const uploadResult = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!uploadResult.ok) {
          throw new Error(`S3 upload failed with status ${uploadResult.status}`);
        }

        await addFileRequest({
          endPoint: `/course/addcoursefile/${clientId}/${courseId}`,
          method: "post",
          data: {
            file_name: file.name,
            file_url: fileUrl,
            file_size: file.size,
            file_type: file.name.split(".").pop()?.toUpperCase() || file.type,
          },
        });
      }
      message.success(`Uploaded ${stagedFiles.length} file(s)`);
      setFileModalOpen(false);
      fetchCourseFiles();
    } catch (error) {
      console.error("Error uploading files:", error);
      message.error("Upload failed");
    } finally {
      setSavingFiles(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteFileRequest({
        endPoint: `/course/deletecoursefile/${clientId}/${courseId}?file_id=${fileId}`,
        method: "delete",
      });
      message.success("File removed");
      fetchCourseFiles();
    } catch (error) {
      message.error("Failed to remove file");
    }
  };

  const handleDownloadFile = (record) => {
    const link = document.createElement("a");
    link.href = record.file_url;
    link.download = record.file_name;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---- Columns --------------------------------------------------

  const peopleColumns = [
    { title: "Employee ID", dataIndex: "people_id", key: "people_id" },
    {
      title: "Employee Name",
      key: "name",
      render: (_, record) => `${record.first_name || ""} ${record.last_name || ""}`.trim() || "-",
    },
    {
      title: "Role",
      dataIndex: "peopel_type",
      key: "peopel_type",
      render: (value) => value || "-",
    },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      render: (value) => (value != null ? `${value}%` : "-"),
    },
    {
      title: "Enrolled",
      dataIndex: "enrolled_at",
      key: "enrolled_at",
      render: (value) => formatDate(value),
    },
    {
      title: "Completed on",
      dataIndex: "completed_at",
      key: "completed_at",
      render: (value) => formatDate(value),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (value) => formatDate(value),
    },
    {
      title: "",
      key: "actions",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <div className="table_action_icons">
          <Popover placement="top" content="Remove">
            <span className="row-action-icon" onClick={() => handleUnenroll(record)}>
              <CloseOutlined />
            </span>
          </Popover>
          <Popover placement="top" content="Mark Complete">
            <span className="row-action-icon" onClick={() => handleMarkComplete(record)}>
              <CheckOutlined />
            </span>
          </Popover>
          <Popover placement="top" content="Reset Progress">
            <span className="row-action-icon" onClick={() => handleResetProgress(record)}>
              <ReloadOutlined />
            </span>
          </Popover>
          <Popover placement="top" content="Renew Expiry">
            <span className="row-action-icon" onClick={() => handleRenewExpiry(record)}>
              <ClockCircleOutlined />
            </span>
          </Popover>
        </div>
      ),
    },
  ];

  const assignedGroupsColumns = [
    { title: "Group Name", dataIndex: "group_name", key: "group_name" },
    {
      title: "",
      key: "remove",
      width: "15%",
      render: (_, record) => (
        <span className="row-remove-link" onClick={() => handleRemoveGroup(record.group_id)}>
          <CloseOutlined /> Remove
        </span>
      ),
    },
  ];

  const filesColumns = [
    {
      title: "File Name",
      dataIndex: "file_name",
      key: "file_name",
      className: "normal-column",
    },
    {
      title: "Type",
      dataIndex: "file_type",
      key: "file_type",
      render: (value) => value || "-",
    },
    {
      title: "Size",
      dataIndex: "file_size",
      key: "file_size",
      render: (value) => formatFileSize(value),
    },
    {
      title: "Uploaded on",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
    },
    {
      title: "",
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <div className="table_action_icons">
          <Popover placement="top" content="View">
            <a className="row-action-icon" href={record.file_url} target="_blank" rel="noreferrer">
              <EyeOutlined />
            </a>
          </Popover>
          <Popover placement="top" content="Download">
            <span className="row-action-icon" onClick={() => handleDownloadFile(record)}>
              <DownloadOutlined />
            </span>
          </Popover>
          <Popover placement="top" content="Delete">
            <span className="row-action-icon row-action-icon-danger" onClick={() => handleDeleteFile(record.file_id)}>
              <DeleteOutlined />
            </span>
          </Popover>
        </div>
      ),
    },
  ];

  const onChangeSortedTable = () => {};

  const items = [
    { id: "1", label: "People", component: <></> },
    { id: "2", label: "Group", component: <></> },
    { id: "3", label: "File", component: <></> },
  ];

  const handleTabSelect = (key) => {
    setActiveTab(key);
  };

  const editCourseButton = (
    <Button
      className="edit-course-outline-button"
      onClick={() => navigate("/CreateCourse", { state: tableViewData })}
    >
      <EditOutlined /> Edit Course
    </Button>
  );

  return (
    <>
      <UsersStyle>
        <div className="user-title-container users-menu-filter-container">
          <div className="course-breadcrumb">
            <Link to="/course" onClick={() => setUserRenderView("table-view")}>
              Course
            </Link>
            <span>&gt;</span>
            <span className="breadcrumb-current">{tableViewData.course_name}</span>
          </div>
        </div>

        <TabsList items={items} handleTabSelect={handleTabSelect} activeKey={activeTab} />

        {activeTab === "1" && (
          <>
            <Row className="users-menu-filter-container">
              <Col>
                <Form.Item name="search" className="search-input-field">
                  <Input prefix={<SearchOutlined />} placeholder="Search" />
                </Form.Item>
              </Col>

              <Col className="header-action-buttons">
                {editCourseButton}
                <Button className="course-enroll-button" onClick={openEnrollModal}>
                  <PlusOutlined /> Enroll Employees
                </Button>
              </Col>
            </Row>
            <StyledTable
              dataSource={enrolledPeople}
              columns={peopleColumns}
              loading={enrollmentsLoading}
              rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
              rowKey="enrollment_id"
              id="Table"
              onChange={onChangeSortedTable}
              showSorterTooltip={{ target: "sorter-icon" }}
              scroll={{ x: "max-content" }}
            />
          </>
        )}

        {activeTab === "2" && (
          <>
            <Row className="single-action-row">
              {editCourseButton}
              <Button className="course-enroll-button" onClick={openGroupModal}>
                <PlusOutlined /> Add to Group
              </Button>
            </Row>
            <StyledTable
              dataSource={courseGroups}
              columns={assignedGroupsColumns}
              loading={groupsLoading}
              rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
              rowKey="group_id"
              id="Table"
            />
          </>
        )}

        {activeTab === "3" && (
          <>
            <Row className="single-action-row">
              {editCourseButton}
              <Button className="course-enroll-button" onClick={openFileModal}>
                <PlusOutlined /> Upload File
              </Button>
            </Row>
            <StyledTable
              dataSource={courseFiles}
              columns={filesColumns}
              loading={filesLoading}
              rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
              rowKey="file_id"
              id="Table"
              scroll={{ x: "max-content" }}
            />
          </>
        )}

        {/* Enroll Employees - step 1: pick list vs. category */}
        <Modal
          title="Enroll Employees"
          open={enrollModalOpen && enrollStep === 1}
          onCancel={closeEnrollModal}
          footer={[
            <Button key="cancel" onClick={closeEnrollModal}>
              Cancel
            </Button>,
            <Button key="next" className="course-enroll-button" onClick={() => setEnrollStep(2)}>
              Next
            </Button>,
          ]}
        >
          <Radio.Group value={enrollMode} onChange={(e) => setEnrollMode(e.target.value)}>
            <Radio value="list" style={{ display: "block", marginBottom: 12 }}>
              Show Employees list
            </Radio>
            <Radio value="category" style={{ display: "block" }}>
              Employees by Category
            </Radio>
          </Radio.Group>
        </Modal>

        {/* Enroll Employees - step 2: multi-select */}
        <Modal
          title="Choose employees to Enroll"
          open={enrollModalOpen && enrollStep === 2}
          onCancel={closeEnrollModal}
          footer={[
            <Button key="back" type="link" onClick={() => setEnrollStep(1)} style={{ float: "left" }}>
              Back
            </Button>,
            <Button key="cancel" onClick={closeEnrollModal}>
              Cancel
            </Button>,
            <Button
              key="enroll"
              className="course-enroll-button"
              onClick={handleBulkEnroll}
              disabled={selectedPeopleIds.length === 0}
              loading={enrolling}
            >
              Enroll
            </Button>,
          ]}
        >
          {enrollMode === "category" && (
            <Select
              style={{ width: "100%", marginBottom: 16 }}
              placeholder="Select a category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
              options={categories.map((c) => ({ value: c, label: c }))}
            />
          )}
          <Checkbox
            checked={selectedPeopleIds.length > 0 && selectedPeopleIds.length === peopleForStep2.length}
            indeterminate={selectedPeopleIds.length > 0 && selectedPeopleIds.length < peopleForStep2.length}
            onChange={(e) => toggleSelectAllPeople(e.target.checked)}
            style={{ marginBottom: 12 }}
          >
            All
          </Checkbox>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {peopleForStep2.map((person) => (
              <div
                key={person.people_id}
                style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}
              >
                <Checkbox
                  checked={selectedPeopleIds.includes(person.people_id)}
                  onChange={() => togglePersonSelected(person.people_id)}
                >
                  {`${person.first_name || ""} ${person.last_name || ""}`.trim() || "-"}
                </Checkbox>
                <span style={{ color: "#636980" }}>{person.people_id}</span>
              </div>
            ))}
          </div>
        </Modal>

        {/* Add to Group */}
        <Modal
          title="Select group to add this course to"
          open={groupModalOpen}
          onCancel={closeGroupModal}
          footer={[
            <Button key="cancel" onClick={closeGroupModal}>
              Cancel
            </Button>,
            <Button
              key="addto"
              className="course-enroll-button"
              onClick={handleBulkAddGroup}
              disabled={selectedGroupIds.length === 0}
              loading={addingGroups}
            >
              Add to
            </Button>,
          ]}
        >
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {selectableGroups.map((group) => (
              <div key={group.group_id} style={{ padding: "8px 0" }}>
                <Checkbox
                  checked={selectedGroupIds.includes(group.group_id)}
                  onChange={() => toggleGroupSelected(group.group_id)}
                >
                  {group.group_name}
                </Checkbox>
              </div>
            ))}
          </div>
        </Modal>

        {/* Upload File */}
        <Modal
          title="Upload File"
          open={fileModalOpen}
          onCancel={closeFileModal}
          footer={[
            <Button key="cancel" onClick={closeFileModal}>
              Cancel
            </Button>,
            <Button
              key="save"
              className="course-enroll-button"
              onClick={handleSaveStagedFiles}
              disabled={stagedFiles.length === 0}
              loading={savingFiles}
            >
              Save
            </Button>,
          ]}
        >
          <div
            className="upload-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingOver(true);
            }}
            onDragLeave={() => setIsDraggingOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDraggingOver(false);
              if (e.dataTransfer.files?.length) addStagedFiles(e.dataTransfer.files);
            }}
            style={isDraggingOver ? { background: "#f3fefa", borderColor: "#2ecc9a" } : undefined}
          >
            <UploadOutlined style={{ fontSize: 24, color: "#636980" }} />
            <div>Drag files here to upload</div>
            <div>
              Alternatively, you can select file by <span className="browse-link">Clicking here</span>
            </div>
          </div>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files?.length) addStagedFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <div style={{ marginTop: 16 }}>
            {stagedFiles.map((file, index) => (
              <div className="staged-file-row" key={`${file.name}-${index}`}>
                <span>{file.name}</span>
                <CloseOutlined onClick={() => removeStagedFile(index)} />
              </div>
            ))}
          </div>
        </Modal>
      </UsersStyle>
    </>
  );
};

export default ViewCourse;
